import { APP_NAME } from '../config';
import type { SidebarCounts } from '../logic/desk';
import { h, span } from './dom';
import { icon } from './icons';
import type { IconName } from './icons';

/** The desktop sidebar (D65): every screen one click away, as in macOS Mail. Hidden below 1024 px. */

interface NavItem {
  href: string;
  label: string;
  icon: IconName;
  count?: keyof SidebarCounts;
  /** The count that asks for attention (new businesses to review) wears the accent. */
  accent?: boolean;
  matches: (path: string) => boolean;
}

const NAV_GROUPS: readonly { label: string | null; items: readonly NavItem[] }[] = [
  {
    label: null,
    items: [
      { href: '#/home', label: 'Overview', icon: 'overview', matches: (p) => p === '/home' },
      { href: '#/brief', label: 'Brief', icon: 'brief', count: 'dueFollowUps', matches: (p) => p === '/brief' },
      {
        href: '#/signals',
        label: 'New businesses',
        icon: 'signals',
        count: 'signals',
        accent: true,
        matches: (p) => p === '/signals',
      },
      {
        href: '#/pipeline',
        label: 'Pipeline · open',
        icon: 'pipeline',
        count: 'pipeline',
        matches: (p) => p === '/pipeline' || p.startsWith('/leads'),
      },
      {
        href: '#/route',
        label: 'Route',
        icon: 'route',
        matches: (p) => p === '/route' || p.startsWith('/route/'),
      },
    ],
  },
  {
    label: 'Follow-through',
    items: [
      { href: '#/signals/sources', label: 'Sources', icon: 'signals', matches: (p) => p === '/signals/sources' },
      { href: '#/import/plan-checks', label: 'Plan checks', icon: 'signals', matches: (p) => p === '/import/plan-checks' },
      {
        href: '#/emails',
        label: 'Emails',
        icon: 'emails',
        count: 'emails',
        matches: (p) => p === '/emails' || p === '/templates',
      },
      {
        href: '#/activity',
        label: 'Activity',
        icon: 'activity',
        matches: (p) => p === '/activity' || p.startsWith('/activity/'),
      },
      { href: '#/dashboard', label: 'Reports', icon: 'reports', matches: (p) => p === '/dashboard' },
      { href: '#/watchlist', label: 'Watchlist', icon: 'watchlist', matches: (p) => p === '/watchlist' },
    ],
  },
];

export interface Sidebar {
  el: HTMLElement;
  setActive: (path: string) => void;
  setCounts: (counts: SidebarCounts) => void;
  setSync: (text: string, state: string) => void;
}

export const mountSidebar = (searchEl: HTMLElement): Sidebar => {
  const items: { item: NavItem; link: HTMLAnchorElement; count: HTMLElement }[] = [];
  const groups = NAV_GROUPS.map((g) =>
    h(
      'nav',
      { class: 'sidebar__group', 'aria-label': g.label ?? 'Main' },
      g.label ? h('div', { class: 'sidebar__grouplabel' }, g.label) : null,
      g.items.map((item) => {
        const count = span(
          ['sidebar__count', item.accent ? 'sidebar__count--accent' : ''].filter(Boolean).join(' '),
        );
        count.hidden = true;
        const link = h(
          'a',
          { class: 'sidebar__item', href: item.href },
          icon(item.icon),
          span('sidebar__label', item.label),
          count,
        );
        items.push({ item, link, count });
        return link;
      }),
    ),
  );
  const syncText = span('sidebar__synctext', 'Synced');
  const sync = h(
    'a',
    { class: 'sidebar__sync', href: '#/sync', dataset: { state: 'idle' } },
    span('sidebar__dot'),
    syncText,
  );
  const el = h(
    'aside',
    { class: 'sidebar', 'aria-label': 'Sidebar' },
    h(
      'a',
      { class: 'sidebar__brand', href: '#/home' },
      h('img', { class: 'sidebar__logo', src: '/icons/icon-192.png', alt: '', width: 28, height: 28 }),
      span('sidebar__name', APP_NAME),
    ),
    searchEl,
    groups,
    sync,
  );
  return {
    el,
    setActive: (path) => {
      for (const { item, link } of items) {
        const on = item.matches(path);
        link.classList.toggle('sidebar__item--active', on);
        if (on) link.setAttribute('aria-current', 'page');
        else link.removeAttribute('aria-current');
      }
    },
    setCounts: (counts) => {
      for (const { item, count } of items) {
        const n = item.count ? counts[item.count] : null;
        count.hidden = !n;
        count.textContent = n ? String(n) : '';
      }
    },
    setSync: (text, state) => {
      syncText.textContent = text;
      sync.dataset.state = state;
    },
  };
};
